import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Project, Folder } from '../services/folder.service';
import { getProjectList } from '../services/project.service';
import { getTestCaseList } from '../services/testcase.service';
import { ITestCase } from '../types/interfaces/testCase.interface';
import { CHANNELS } from '../types/global.enum';
import { findProject, getProjectIdsInFolders } from '../helpers/folder.utils';
import { useAppDispatch } from '../store/hooks';
import { setCurrentProject } from '../store/resuders/project.reducer';
import { clearSegments, setEditSegment, upsertLibrary } from '../store/resuders/segment.reducer';


// State for tracking loaded test cases for each project
interface TestCaseLoadingState {
  loading: boolean;
  refreshing?: boolean; // Background refresh while keeping existing data visible
  data: ITestCase[] | null; // Use null to indicate "not loaded yet"
  error?: string | null;
}

// Props needed by the hook
interface ProjectItemsProps {
  folders: Folder[]; // From useFolderData
  foldersLoading: boolean; // From useFolderData
  preloadedTestCases?: Record<number, TestCaseLoadingState>; // From new API
  isUsingNewAPI?: boolean; // Flag to indicate new API usage
}

// Session storage key for test case state
const FOLDER_VIEW_TESTCASE_STATE_KEY = 'folderViewTestCaseState';

const loadInitialTestCaseState = (): Record<number, TestCaseLoadingState> => {
    try {
        const storedState = sessionStorage.getItem(FOLDER_VIEW_TESTCASE_STATE_KEY);
        if (storedState) {
            return JSON.parse(storedState);
        }
    } catch (e) {
        console.error("Error reading test case state from sessionStorage", e);
        sessionStorage.removeItem(FOLDER_VIEW_TESTCASE_STATE_KEY);
    }
    return {}; // Default empty state
};


export function useProjectItems({ folders, foldersLoading, preloadedTestCases = {}, isUsingNewAPI = false }: ProjectItemsProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [loadedTestCases, setLoadedTestCases] = useState<Record<number, TestCaseLoadingState>>({});
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [allProjectsLoading, setAllProjectsLoading] = useState<boolean>(true);
  const [initialTestCaseStateLoaded, setInitialTestCaseStateLoaded] = useState(false);
  
  // Background loading state
  const [backgroundLoading, setBackgroundLoading] = useState<boolean>(false);
  const [backgroundLoadingProgress, setBackgroundLoadingProgress] = useState<{loaded: number, total: number}>({loaded: 0, total: 0});


  // Load initial test case state - either from preloaded data or session storage
  useEffect(() => {
      if (isUsingNewAPI && Object.keys(preloadedTestCases).length > 0) {
          // Use preloaded data from new API
          console.log(`Using preloaded test cases for ${Object.keys(preloadedTestCases).length} projects from new API`);
          setLoadedTestCases(preloadedTestCases);
          setInitialTestCaseStateLoaded(true);
      } else if (!isUsingNewAPI) {
          // Load from session storage for old API
          setLoadedTestCases(loadInitialTestCaseState());
          setInitialTestCaseStateLoaded(true);
      }
  }, [preloadedTestCases, isUsingNewAPI]);


  // Function to fetch all projects (extracted for reuse)
  const fetchAllProjects = useCallback(async (setLoading = true) => {
    if (setLoading) setAllProjectsLoading(true);
    try {
      // Fetch first 100 projects, sorted by last modified time descending
      const { data } = await getProjectList(0, 100, '-lastModifiedTime', '');
      setAllProjects(data?.content || []);
    } catch (error) {
      console.error('Failed to fetch all projects:', error);
      setAllProjects([]); // Set to empty on error
    } finally {
      if (setLoading) setAllProjectsLoading(false);
    }
  }, []); // Empty dependency array: Function itself doesn't depend on state


  // Fetch all projects on mount
  useEffect(() => {
    fetchAllProjects(true); // Call the extracted function
  }, [fetchAllProjects]); // Add fetchAllProjects to dependency array


  // Save loaded test case state to session storage
  useEffect(() => {
      if (initialTestCaseStateLoaded) {
          try {
              const stateToSave = JSON.stringify(loadedTestCases);
              sessionStorage.setItem(FOLDER_VIEW_TESTCASE_STATE_KEY, stateToSave);
          } catch (e) {
              console.error("Error saving test case state to sessionStorage", e);
          }
      }
  }, [loadedTestCases, initialTestCaseStateLoaded]);


  // Calculate the set of project IDs present in the folder structure
  const projectIdsInFolders = useMemo(() => {
    return getProjectIdsInFolders(folders);
  }, [folders]);

  // Filter all projects to get only those not in the folder structure
  const unsortedProjects = useMemo(() => {
    // Ensure allProjects has loaded before filtering
    if (allProjectsLoading) return [];
    return allProjects.filter(p => !projectIdsInFolders.has(p.testProjectID));
  }, [allProjects, projectIdsInFolders, allProjectsLoading]);


  // Function to load test cases for a specific project
  const loadProjectTestCases = useCallback(async (projectId: number, forceRefresh: boolean = false, backgroundRefresh: boolean = false) => {
    // If using new API and data exists, skip unless forcing refresh
    if (isUsingNewAPI && loadedTestCases[projectId]?.data && !forceRefresh && !backgroundRefresh) {
        console.log(`Using preloaded test cases for project ${projectId} from new API`);
        return;
    }
    
    // Prevent redundant fetches *only* if data is already loaded AND not forcing refresh
    // Allow fetch if loading is true (because refresh sets it to true)
    if (!forceRefresh && !backgroundRefresh && loadedTestCases[projectId]?.data) { 
        console.log(`Skipping fetch for project ${projectId}, data already loaded.`);
        return;
    }

    // Find project details to determine channel
    let project = findProject(folders, projectId);
    if (!project && !allProjectsLoading) {
        project = allProjects.find(p => p.testProjectID === projectId);
    }

    if (!project) {
        if (foldersLoading || allProjectsLoading) {
            // Data is still loading, wait and retry
            console.warn(`Project ${projectId} not found yet (data loading), retrying in 300ms...`);
            setTimeout(() => loadProjectTestCases(projectId), 300);
            return;
        } else {
            console.error(`Project ${projectId} not found in folders or allProjects list.`);
            setLoadedTestCases(prev => ({
                ...prev,
                [projectId]: { loading: false, data: null, error: 'Project not found' }
            }));
            return;
        }
    }

    let channel: CHANNELS;
    if (project.channelType?.toUpperCase() === 'DIGITAL') {
        channel = CHANNELS.DIGITAL;
    } else if (project.channelType?.toUpperCase() === 'VOICE') {
        channel = CHANNELS.VOICE;
    } else {
        console.warn(`Unknown channelType '${project.channelType}' for project ${projectId}. Defaulting to VOICE.`);
        channel = CHANNELS.VOICE; // Or your preferred default
    }

    // Set appropriate loading state based on refresh mode
    if (backgroundRefresh && loadedTestCases[projectId]?.data) {
      // Background refresh: keep existing data visible, just add refreshing flag
      setLoadedTestCases(prev => ({ 
        ...prev, 
        [projectId]: { 
          ...prev[projectId], 
          refreshing: true, 
          error: null 
        } 
      }));
    } else {
      // Regular load: clear data and show loading
      setLoadedTestCases(prev => ({ ...prev, [projectId]: { loading: true, data: null, error: null } }));
    }

    try {
        const sortBy = '-lastModifiedTime';
        const pageSize = 10000;
        const result = await getTestCaseList(projectId, channel, 0, pageSize, sortBy);
        const testCases = result?.data?.content || [];
        setLoadedTestCases(prev => ({ 
          ...prev, 
          [projectId]: { 
            loading: false, 
            refreshing: false, 
            data: testCases, 
            error: null 
          } 
        }));
    } catch (err: any) {
        console.error(`Failed to load test cases for project ${projectId}`, err);
        if (backgroundRefresh && loadedTestCases[projectId]?.data) {
          // Background refresh failed: keep existing data, just clear refreshing flag
          setLoadedTestCases(prev => ({ 
            ...prev, 
            [projectId]: { 
              ...prev[projectId], 
              refreshing: false, 
              error: 'Refresh failed - showing cached data' 
            } 
          }));
        } else {
          // Regular load failed: clear data and show error
          setLoadedTestCases(prev => ({ ...prev, [projectId]: { loading: false, data: null, error: 'Failed to load' } }));
        }
    }
  }, [loadedTestCases, folders, allProjects, foldersLoading, allProjectsLoading, isUsingNewAPI]); // Dependencies


  // Function to refresh test cases for a specific project
  const refreshProjectTestCases = useCallback(async (projectId: number) => {
    console.log(`Refreshing test cases for project ${projectId}`);
    // Use background refresh to keep existing data visible
    await loadProjectTestCases(projectId, true, true);
  }, [loadProjectTestCases]); // Depends on loadProjectTestCases


  // Function to batch refresh test cases for multiple projects (for auto-refresh)
  const refreshMultipleProjects = useCallback(async (projectIds: number[]) => {
    if (projectIds.length === 0) return;
    
    console.log(`Auto-refreshing ${projectIds.length} projects:`, projectIds);
    
    // Filter to only refresh projects that have loaded test cases
    const projectsToRefresh = projectIds.filter(id => loadedTestCases[id]?.data);
    
    if (projectsToRefresh.length === 0) {
      console.log('No projects with loaded test cases to refresh');
      return;
    }
    
    // Batch refresh with small delays between each to avoid overwhelming backend
    for (let i = 0; i < projectsToRefresh.length; i++) {
      const projectId = projectsToRefresh[i];
      try {
        await loadProjectTestCases(projectId, true, true); // Use background refresh
        // Small delay between refreshes if there are more projects
        if (i < projectsToRefresh.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      } catch (error) {
        console.warn(`Failed to refresh project ${projectId} during batch refresh:`, error);
      }
    }
    
    console.log(`Batch refresh completed for ${projectsToRefresh.length} projects`);
  }, [loadedTestCases, loadProjectTestCases]);


  // Function to clear loaded test cases state (e.g., after project deletion)
  const clearLoadedTestCasesForProject = useCallback((projectIds: number[]) => {
      setLoadedTestCases(prev => {
          const newState = { ...prev };
          projectIds.forEach(id => delete newState[id]);
          return newState;
      });
  }, []);


  // Background loading function to load all project test cases
  const loadAllProjectTestCasesInBackground = useCallback(async () => {
    if (foldersLoading || allProjectsLoading) {
      return; // Wait for initial data to load
    }

    // Get all unique project IDs from folders and unsorted projects
    const allProjectIds = new Set<number>();
    
    // Add projects from folder structure
    const addProjectsFromFolders = (folderList: Folder[]) => {
      folderList.forEach(folder => {
        folder.projects.forEach(project => allProjectIds.add(project.testProjectID));
        if (folder.folders) {
          addProjectsFromFolders(folder.folders);
        }
      });
    };
    addProjectsFromFolders(folders);
    
    // Add unsorted projects
    allProjects.forEach(project => allProjectIds.add(project.testProjectID));

    const projectIdsArray = Array.from(allProjectIds);
    
    // Filter out projects that already have loaded test cases
    const projectsToLoad = projectIdsArray.filter(id => !loadedTestCases[id]?.data);
    
    if (projectsToLoad.length === 0) {
      return; // Nothing to load
    }

    console.log(`Starting background loading of test cases for ${projectsToLoad.length} projects`);
    setBackgroundLoading(true);
    setBackgroundLoadingProgress({ loaded: 0, total: projectsToLoad.length });

    // Load projects one by one with progress tracking
    let loaded = 0;
    for (const projectId of projectsToLoad) {
      try {
        await loadProjectTestCases(projectId);
        loaded++;
        setBackgroundLoadingProgress({ loaded, total: projectsToLoad.length });
      } catch (error) {
        console.warn(`Failed to load test cases for project ${projectId} during background loading:`, error);
        loaded++; // Still count as processed even if failed
        setBackgroundLoadingProgress({ loaded, total: projectsToLoad.length });
      }
    }

    setBackgroundLoading(false);
    console.log(`Background loading completed. Loaded test cases for ${loaded}/${projectsToLoad.length} projects`);
  }, [folders, allProjects, foldersLoading, allProjectsLoading, loadedTestCases, loadProjectTestCases]);


  // Trigger background loading when initial data is ready (skip for new API)
  useEffect(() => {
    if (!foldersLoading && !allProjectsLoading && initialTestCaseStateLoaded && !backgroundLoading) {
      // Skip background loading if using new API (data already preloaded)
      if (isUsingNewAPI) {
        console.log('Skipping background test case loading - all data preloaded from new tree API');
        return;
      }
      
      // Small delay to let the UI settle before starting background loading
      const timer = setTimeout(() => {
        loadAllProjectTestCasesInBackground();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [foldersLoading, allProjectsLoading, initialTestCaseStateLoaded, backgroundLoading, loadAllProjectTestCasesInBackground, isUsingNewAPI]);


  // Handler for opening a project
  const handleOpenProject = useCallback((projectId: number) => {
    let project = findProject(folders, projectId);
    if (!project) {
      project = allProjects.find(p => p.testProjectID === projectId);
    }

    if (project) {
      dispatch(setCurrentProject(project));
      dispatch(upsertLibrary(''));
      dispatch(setEditSegment(null));
      dispatch(clearSegments());
      navigate(`/folders/project/${projectId}/testcases`);
    } else {
      console.error(`Project ${projectId} not found when trying to open.`);
      alert(`Error: Could not find project details for ID ${projectId}.`);
    }
  }, [folders, allProjects, dispatch, navigate]);


  // Handler for opening a test case
  const handleOpenTestCase = useCallback((projectId: number, testCaseId: number) => {
    navigate(`/folders/project/${projectId}/testcase/${testCaseId}`);
  }, [navigate]);

  // Handler for opening a test case in a new tab
  const handleOpenTestCaseInNewTab = useCallback((projectId: number, testCaseId: number) => {
    const url = `/folders/project/${projectId}/testcase/${testCaseId}`;
    window.open(url, '_blank');
  }, []);

  // Handler for opening run results for a specific test case
  const handleOpenTestCaseResults = useCallback((projectId: number, testCaseId: number) => {
    const path = `/folders/project/${projectId}/testresults/${testCaseId}`;
    console.log('[projectItems.hooks.ts] Navigating to Test Case Results:', path);
    navigate(path);
  }, [navigate]);


  // Handler for opening the Scheduler view in full-screen mode
  const handleOpenScheduler = useCallback((projectId: number) => {
    const path = `/projects/${projectId}/scheduler`;
    console.log('[projectItems.hooks.ts] Navigating to Scheduler:', path);
    navigate(path);
  }, [navigate]);


  // Handler for opening the Test Results view under folders
  const handleOpenTestResults = useCallback((projectId: number) => {
    const path = `/folders/project/${projectId}/testresults`;
    console.log('[projectItems.hooks.ts] Navigating to Test Results:', path);
    navigate(path);
  }, [navigate]);

  // Handler for opening the Scheduled Test Results view
  const handleOpenScheduledTestResults = useCallback((projectId: number) => {
    const path = `/projects/${projectId}/scheduled/testresults`;
    console.log('[projectItems.hooks.ts] Navigating to Scheduled Test Results:', path);
    navigate(path);
  }, [navigate]);


  return {
    allProjects, // Maybe not needed directly by Folders.tsx, but useful for context
    allProjectsLoading,
    unsortedProjects,
    loadedTestCases,
    loadProjectTestCases,
    refreshProjectTestCases,
    refreshMultipleProjects, // Expose batch refresh function for auto-refresh
    clearLoadedTestCasesForProject, // Expose the clear function
    handleOpenProject,
    handleOpenTestCase,
    handleOpenTestCaseInNewTab,
    handleOpenTestCaseResults,
    handleOpenScheduler,
    handleOpenScheduledTestResults,
    handleOpenTestResults,
    refetchAllProjects: fetchAllProjects, // Expose the refetch function
    // Background loading state
    backgroundLoading,
    backgroundLoadingProgress,
  };
}
